import React from "react";
import { Button, Modal } from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import { updateOrder, getIccidByVariant } from "../api";
import styles from "../styles/modals.module.css";

const IccidModal = ({
  visible,
  orderData,
  iccidList,
  selectedIccids,
  iccidLoading,
  submitting,
  onClose,
  onSuccess,
  setIccidList,
  setSelectedIccids,
  setIccidLoading,
  setSubmitting,
  onAddManually = () => {},
}) => {
  const handleIccidModalOpen = async () => {
    if (orderData) {
      setIccidLoading(true);
      
      try {
        // Assuming variantId comes from orderData, adjust according to your data structure
        const variantId = orderData.variantId || orderData._id; // adjust this based on your data
        const response = await getIccidByVariant(variantId);
        setIccidList(response.data || []);
      } catch (error) {
        toast.error('Failed to fetch ICCID data');
        console.error('Error fetching ICCID:', error);
      } finally {
        setIccidLoading(false);
      }
    }
  };

  const handleIccidSelection = (iccidId) => {
    setSelectedIccids(
      selectedIccids.includes(iccidId)
        ? selectedIccids.filter(id => id !== iccidId)
        : [...selectedIccids, iccidId]
    );
  };

  const handleIccidAssignment = async () => {
    if (selectedIccids.length === 0) {
      toast.error('Please select at least one ICCID');
      return;
    }

    setSubmitting(true);

    try {
      // Get the selected ICCID details
      const selectedIccidData = iccidList.filter(iccid => 
        selectedIccids.includes(iccid._id || iccid.id)
      );

      const payload = {
        iccids: selectedIccidData.map(iccid => ({
          orderId: iccid._id || iccid.id,
          icciId: iccid.iccid
        })),
      };

      const payload2 = {
        iccids: selectedIccidData.map(iccid => ({
          iccid: iccid.iccid
        })),
      };


      await updateOrder( orderData?._id,  payload2?.iccids[0]);
      
      toast.success(`${selectedIccids.length} ICCID(s) assigned successfully`);
      onSuccess();
    } catch (error) {
      toast.error('Failed to assign ICCIDs');
      console.error('Error assigning ICCIDs:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Load ICCID data when modal becomes visible
  React.useEffect(() => {
    if (visible && orderData && iccidList.length === 0) {
      handleIccidModalOpen();
    }
  }, [visible, orderData]);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Assign ICCID</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        {iccidLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}>
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
            <p className={styles.loadingText}>Loading ICCID data...</p>
          </div>
        ) : (
          <>
            {iccidList.length > 0 ? (
              <>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead className={styles.tableHeader}>
                      <tr>
                        <th className={styles.tableHeaderCell}>
                          <span>Select</span>
                        </th>
                        <th className={styles.tableHeaderCell}>ICCID</th>
                        <th className={styles.tableHeaderCell}>Status</th>
                        <th className={styles.tableHeaderCell}>Variant</th>
                        <th className={styles.tableHeaderCell}>Created Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {iccidList.map((iccid, index) => (
                        <tr key={iccid._id || iccid.id || index} className={styles.tableRow}>
                          <td className={styles.tableCell}>
                            <input
                              type="checkbox"
                              checked={selectedIccids.includes(iccid._id || iccid.id)}
                              onChange={() => handleIccidSelection(iccid._id || iccid.id)}
                              className={styles.checkbox}
                            />
                          </td>
                          <td className={styles.tableCell}>
                            {iccid.iccidNumber || iccid.iccid || 'N/A'}
                          </td>
                          <td className={styles.tableCell}>
                            <span 
                              className={`${styles.badge} ${
                                iccid.status === 'active' ? styles.badgeSuccess : styles.badgeSecondary
                              }`}
                            >
                              {iccid.status || 'Unknown'}
                            </span>
                          </td>
                          <td className={styles.tableCell}>
                            {iccid.variant || iccid.variantName || 'N/A'}
                          </td>
                          <td className={styles.tableCell}>
                            {iccid.createdAt ? moment(iccid.createdAt).format("DD/MM/YYYY") : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                  <Button type="primary" onClick={onAddManually}>
                    Add Manually
                  </Button>
                  <div className={styles.buttonGroup}>
                    <Button 
                      onClick={onClose}
                      className={styles.cancelButton}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="primary" 
                      disabled={selectedIccids.length === 0}
                      onClick={handleIccidAssignment}
                      loading={submitting}
                      className={styles.submitButton}
                    >
                      Assign Selected
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <p className={styles.emptyStateText}>No ICCID data available for this variant.</p>
                <Button type="primary" onClick={onAddManually}>
                  Add Manually
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default IccidModal; 